import getDistance from "@turf/rhumb-distance";
import getBearing from "@turf/rhumb-bearing";

export type LngLat = [number, number];

interface Waypoint {
  point: LngLat;
  heading?: number;
  time: number;
}

interface TripBuilderOptions {
  waypoints: LngLat[];
  speed?: number;      // metros por segundo
  turnSpeed?: number;  // graus por segundo
  loop?: boolean;
}

export default class TripBuilder {
  public keyframes: Waypoint[] = [];

  private speed: number;
  private turnSpeed: number;
  private loop: boolean;
  private totalTime = 0;

  constructor({
    waypoints,
    speed = 100,      // Velocidade de reprodução: mantém o ritmo anterior com a distância em metros.
    turnSpeed = 180,  // 🚀 curva mais rápida
    loop = false,
  }: TripBuilderOptions) {
    this.speed = Number.isFinite(speed) && speed > 0 ? speed : 100;
    this.turnSpeed = Number.isFinite(turnSpeed) && turnSpeed > 0 ? turnSpeed : 180;
    this.loop = loop;

    for (const p of waypoints) {
      this.moveTo(p);
    }

    if (loop && this.keyframes.length > 2) {
      this.moveTo(this.keyframes[0].point);
      this.turnTo(this.keyframes[0].heading ?? 0);
    }

    if (this.keyframes.length === 1) {
      this.keyframes[0].heading = 0;
    }
  }

  private moveTo(point: LngLat) {
    if (!Array.isArray(point) || !Number.isFinite(point[0]) || !Number.isFinite(point[1])
      || Math.abs(point[0]) > 180 || Math.abs(point[1]) > 90) return;

    if (this.keyframes.length === 0) {
      this.keyframes.push({ point, time: 0 });
      return;
    }

    const prev = this.keyframes[this.keyframes.length - 1];

    const distance = getDistance(prev.point, point, { units: "meters" });

    if (!Number.isFinite(distance) || distance < 1) return;

    const heading = getBearing(prev.point, point);
    if (!Number.isFinite(heading)) return;

    if (prev.heading === undefined) {
      prev.heading = heading;
    } else {
      this.turnTo(heading);
    }

    const duration = distance / this.speed;
    this.totalTime += duration;

    this.keyframes.push({
      point,
      heading,
      time: this.totalTime,
    });
  }

  private turnTo(heading: number) {
    const prev = this.keyframes[this.keyframes.length - 1];
    const angle = Math.abs(this.getTurnAngle(prev.heading!, heading));

    if (angle === 0) return;

    const duration = angle / this.turnSpeed;
    this.totalTime += duration;

    this.keyframes.push({
      point: prev.point,
      heading,
      time: this.totalTime,
    });
  }

  private getTurnAngle(start: number, end: number) {
    let delta = end - start;
    if (delta > 180) delta -= 360;
    if (delta < -180) delta += 360;
    return delta;
  }

  public getFrame(timestamp: number): { point: LngLat; heading: number } | undefined {
    if (!this.keyframes.length) return undefined;
    const first = this.keyframes[0];
    if (this.totalTime <= 0) return { point: first.point, heading: first.heading ?? 0 };

    const safeTimestamp = Number.isFinite(timestamp) ? Math.max(0, timestamp) : 0;

    const t = this.loop
      ? safeTimestamp % this.totalTime
      : Math.min(safeTimestamp, this.totalTime);

    const i = this.keyframes.findIndex((k) => k.time >= t);

    if (i === -1) {
      const last = this.keyframes[this.keyframes.length - 1];
      return { point: last.point, heading: last.heading ?? 0 };
    }
    if (i === 0) return { point: first.point, heading: first.heading ?? 0 };

    const a = this.keyframes[i - 1];
    const b = this.keyframes[i];
    const duration = b.time - a.time;
    const r = duration > 0 ? (t - a.time) / duration : 0;

    return {
      point: [
        a.point[0] + (b.point[0] - a.point[0]) * r,
        a.point[1] + (b.point[1] - a.point[1]) * r,
      ],
      heading: (a.heading ?? 0) + this.getTurnAngle(a.heading ?? 0, b.heading ?? 0) * r,
    };
  }
}
